import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { CalendarCheck, Mail, Phone, PhoneOff, X } from "lucide-react"
import { api } from "@/lib/api"
import { UebernehmenKnopf } from "@/components/portal/UebernehmenKnopf"

/**
 * Globaler Live-Banner-Stack (oben rechts, unter der GlobalSearch) — auf
 * JEDER Seite sichtbar. Drei Kartentypen:
 *
 *  - Aktiver Anruf (rot pulsierend = klingelt, gruen = im Gespraech)
 *  - Beendeter Anruf ("Anruf beendet"/"verpasst", Klick -> /sipgate/{id}
 *    mit Zusammenfassung; 15-Min-Fenster)
 *  - Neue E-Mail (indigo, Klick -> /emails/{id}; 15-Min-Fenster)
 *  - Terminlink-Rückmeldung (gruen, Klick -> /rueckmeldungen; 15-Min-Fenster,
 *    Paul 01.10.2026: „Rückmeldungen brauchen auch push benachrichtigungen
 *    wie emails!") — Kunde hat gewählt / Wunsch genannt / Link abgelaufen
 *
 * Jede Karte hat ein X zum Wegklicken — Dismissals liegen in localStorage
 * (pro Browser, letzte 100). Datenquellen: /api/v1/sipgate/live (3s-Poll)
 * und /api/v1/emails/live (5s-Poll), jeweils nur bei aktivem Tab.
 */

type LiveTerminlink = {
  /** Zahl = Einzel-Link, „g<id>“ = Abstimmung mit mehreren Personen */
  id: number | string
  tenant_slug: string
  status: string
  titel: string
  kunde: string
  vorgang: string
  detail: string
  zeit: string | null
}

type LiveCall = {
  call_id: string
  direction: string
  caller: string
  callee: string
  status: string
  channel_name: string
  tenant_slug: string
  hero_contact_name?: string | null
  created_at?: string | null
  answered_at?: string | null
  /** #194: Voice-Anruf-ID + Status, wenn die KI den Anruf gerade führt */
  ki_call_id?: string | null
  ki_status?: string | null
}

type EndedCall = {
  id: number
  call_id: string
  caller: string
  callee: string
  status: string
  channel_name: string
  tenant_slug: string
  hero_contact_name?: string | null
  hung_up_at?: string | null
  duration_secs?: number | null
}

type LiveMail = {
  id: number
  sender?: string | null
  subject?: string | null
  tenant_slug?: string | null
  category?: string | null
  created_at?: string | null
}

const DISMISS_KEY = "live-banner-dismissed"

function getDismissed(): string[] {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]") } catch { return [] }
}

function addDismissed(key: string) {
  localStorage.setItem(DISMISS_KEY, JSON.stringify([...getDismissed(), key].slice(-100)))
}

function senderName(raw: string): string {
  // '"Anna Testral" <testral@example.com>' -> Anna Testral; sonst Adresse
  const m = raw.match(/^"?([^"<]+)"?\s*</)
  return (m ? m[1] : raw).trim()
}

function fmtNumber(n: string): string {
  if (/^49\d+$/.test(n)) return `+${n}`
  return n
}

function fmtElapsed(startIso: string | null | undefined, now: number): string {
  if (!startIso) return ""
  const start = new Date(startIso.replace(" ", "T")).getTime()
  if (isNaN(start)) return ""
  const s = Math.max(0, Math.floor((now - start) / 1000))
  const m = Math.floor(s / 60)
  return m > 0 ? `${m}:${String(s % 60).padStart(2, "0")} min` : `${s}s`
}

function fmtAgo(iso: string | null | undefined, now: number): string {
  if (!iso) return ""
  // created_at (Mails) ist naive UTC -> Z anhaengen; hung_up_at hat TZ
  const base = iso.replace(" ", "T")
  const norm = base.includes("+") || base.endsWith("Z") ? base : base + "Z"
  const t = new Date(norm).getTime()
  if (isNaN(t)) return ""
  const s = Math.max(0, Math.floor((now - t) / 1000))
  if (s < 60) return "gerade eben"
  return `vor ${Math.floor(s / 60)} min`
}

function fmtDuration(secs: number | null | undefined): string {
  if (!secs || secs <= 0) return ""
  const m = Math.floor(secs / 60)
  return m > 0 ? `${m}:${String(secs % 60).padStart(2, "0")} min` : `${secs}s`
}

/** Gemeinsamer Karten-Rahmen mit Icon, Inhalt und Wegklick-X. */
function BannerCard({ accent, icon, pulse, label, tenant, title, lines, onClick, onDismiss, extra }: {
  accent: string
  icon: React.ReactNode
  pulse?: boolean
  label: string
  tenant?: string
  title: string
  lines: string[]
  onClick: () => void
  onDismiss: () => void
  /** zusätzlicher Inhalt unter den Zeilen (z. B. Knopf); Klicks darauf navigieren nicht */
  extra?: React.ReactNode
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
        background: "#fff", borderRadius: 12, padding: "10px 12px",
        border: `1px solid ${accent}55`,
        boxShadow: "0 8px 28px rgba(15,23,42,0.16)", minWidth: 270, maxWidth: 370,
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: accent, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        animation: pulse ? "lcb-pulse 1.6s ease-out infinite" : undefined,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: accent, whiteSpace: "nowrap" }}>
            {label}
          </span>
          {tenant && (
            // Tenant-Badge: bei einer Person, die ALLE Mandanten betreut, muss
            // sofort klar sein, von welchem die Meldung stammt.
            <span style={{
              fontSize: 12, fontWeight: 600, color: "#475569", background: "#f1f5f9",
              border: "1px solid #e2e8f0", borderRadius: 5, padding: "1px 6px",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150,
            }}>
              {tenant}
            </span>
          )}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </div>
        {lines.filter(Boolean).map((l, i) => (
          <div key={i} style={{ fontSize: 12, color: i === 0 ? "#334155" : "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {l}
          </div>
        ))}
        {extra && <div onClick={(ev) => ev.stopPropagation()}>{extra}</div>}
      </div>
      <button
        title="Ausblenden"
        onClick={(ev) => { ev.stopPropagation(); onDismiss() }}
        style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8", padding: 4, flexShrink: 0 }}
      >
        <X style={{ width: 14, height: 14 }} />
      </button>
    </div>
  )
}

export function LiveCallBanner() {
  const navigate = useNavigate()
  const [now, setNow] = useState(Date.now())
  const [, setDismissTick] = useState(0)
  const dismiss = (key: string) => { addDismissed(key); setDismissTick((v) => v + 1) }
  const dismissed = getDismissed()

  // Slug -> lesbarer Tenant-Name fuer das Karten-Badge. Gleicher queryKey wie
  // FilterBar → geteilter Cache, kein Extra-Request. Fallback: der rohe Slug.
  const tenantsQ = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/tenants", {})
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 60 * 5,
  })
  const tenantName = (slug?: string | null): string => {
    if (!slug) return ""
    const hit = tenantsQ.data?.items?.find((t) => t.slug === slug)
    return hit?.name || slug
  }

  const callsQ = useQuery({
    queryKey: ["sipgate-live-calls"],
    queryFn: async (): Promise<{ calls: LiveCall[]; ended?: EndedCall[] }> => {
      const r = await fetch("/api/v1/sipgate/live", { credentials: "include" })
      if (!r.ok) return { calls: [], ended: [] }
      return r.json()
    },
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })

  const mailsQ = useQuery({
    queryKey: ["live-new-mails"],
    queryFn: async (): Promise<{ items: LiveMail[] }> => {
      const r = await fetch("/api/v1/emails/live", { credentials: "include" })
      if (!r.ok) return { items: [] }
      return r.json()
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })

  const terminlinksQ = useQuery({
    queryKey: ["live-terminlinks"],
    queryFn: async (): Promise<{ items: LiveTerminlink[] }> => {
      const r = await fetch("/api/v1/terminlinks/live", { credentials: "include" })
      if (!r.ok) return { items: [] }
      return r.json()
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })

  const active = (callsQ.data?.calls || [])
    .filter((c) => c.direction === "in" && !dismissed.includes(`call:${c.call_id}`))
  const activeIds = new Set((callsQ.data?.calls || []).map((c) => c.call_id))
  const ended = (callsQ.data?.ended || [])
    .filter((c) => !activeIds.has(c.call_id) && !dismissed.includes(`ended:${c.call_id}`))
  const mails = (mailsQ.data?.items || [])
    .filter((m) => !dismissed.includes(`mail:${m.id}`))

  // Schluessel mit Status: wer „gewählt“ weggeklickt hat, sieht den Ablauf
  // desselben Links trotzdem.
  const tlKey = (t: LiveTerminlink) => `tl:${t.id}:${t.status}`
  const terminlinks = (terminlinksQ.data?.items || [])
    .filter((t) => !dismissed.includes(tlKey(t)))

  const total = active.length + ended.length + mails.length + terminlinks.length

  // Sekundenticker fuer Dauer/"vor x min" — nur solange etwas angezeigt wird
  useEffect(() => {
    if (!total) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [total])

  if (!total) return null

  return (
    <div style={{ position: "fixed", top: "calc(64px + var(--stoerung-h, 0px))", right: 24, zIndex: 99, display: "flex", flexDirection: "column", gap: 8 }}>
      <style>{`
        @keyframes lcb-pulse {
          0% { box-shadow: 0 0 0 0 rgba(220,38,38,0.45); }
          70% { box-shadow: 0 0 0 10px rgba(220,38,38,0); }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
        }
        @keyframes lcb-ring {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(-14deg); }
          20% { transform: rotate(12deg); }
          30% { transform: rotate(-10deg); }
          40% { transform: rotate(8deg); }
          50% { transform: rotate(0deg); }
        }
      `}</style>

      {active.map((c) => {
        const ringing = c.status === "ringing"
        const who = (c.hero_contact_name || "").trim() || fmtNumber(c.caller) || "Unbekannt"
        return (
          <BannerCard
            key={`call-${c.call_id}`}
            accent={ringing ? "#dc2626" : "#16a34a"}
            pulse={ringing}
            icon={<Phone style={{ width: 18, height: 18, animation: ringing ? "lcb-ring 1.2s ease-in-out infinite" : undefined }} />}
            label={ringing ? "Eingehender Anruf" : "Im Gespräch"}
            tenant={tenantName(c.tenant_slug)}
            title={who}
            lines={[
              [
                c.hero_contact_name ? fmtNumber(c.caller) : "",
                fmtElapsed(ringing ? c.created_at : c.answered_at || c.created_at, now),
              ].filter(Boolean).join(" · "),
              "Alles zu diesem Anrufer →",
            ]}
            // Waehrend das Telefon klingelt zaehlt genau eins: wer ist das und
            // was laeuft bei dem. Der Klick fuehrt darum in die Vollansicht zur
            // Rufnummer, nicht in die Anrufliste.
            onClick={() => navigate(
              `/projekte?phone=${encodeURIComponent(c.caller)}`
              + (c.tenant_slug ? `&tenant=${encodeURIComponent(c.tenant_slug)}` : "")
            )}
            onDismiss={() => dismiss(`call:${c.call_id}`)}
            extra={c.ki_status === "in_progress" && c.ki_call_id
              ? <UebernehmenKnopf call={{ call_id: c.ki_call_id, status: c.ki_status }} />
              : undefined}
          />
        )
      })}

      {ended.map((c) => {
        const missed = c.status === "missed"
        const who = (c.hero_contact_name || "").trim() || fmtNumber(c.caller) || "Unbekannt"
        return (
          <BannerCard
            key={`ended-${c.call_id}`}
            accent={missed ? "#d97706" : "#475569"}
            icon={<PhoneOff style={{ width: 18, height: 18 }} />}
            label={missed ? "Anruf verpasst" : "Anruf beendet"}
            tenant={tenantName(c.tenant_slug)}
            title={who}
            lines={[
              [
                c.hero_contact_name ? fmtNumber(c.caller) : "",
                fmtDuration(c.duration_secs),
                fmtAgo(c.hung_up_at, now),
              ].filter(Boolean).join(" · "),
              "Zur Zusammenfassung →",
            ]}
            // Ist der Anruf vorbei, will man die Zusammenfassung lesen — nicht
            // mehr die Stammdaten. Die steht auf der Anruf-Detailseite
            // (KI-Headline + Zusammenfassung + Transkript). Bis 12.08.2026
            // fuehrte der Klick nach /ki-telefon, was der Kommentar oben schon
            // nicht mehr behauptete.
            onClick={() => { dismiss(`ended:${c.call_id}`); navigate(`/sipgate/${c.id}`) }}
            onDismiss={() => dismiss(`ended:${c.call_id}`)}
          />
        )
      })}

      {mails.map((m) => (
        <BannerCard
          key={`mail-${m.id}`}
          accent="#4f46e5"
          icon={<Mail style={{ width: 18, height: 18 }} />}
          label="Neue E-Mail"
          tenant={tenantName(m.tenant_slug)}
          title={senderName(m.sender || "") || "Unbekannt"}
          lines={[
            m.subject || "(ohne Betreff)",
            fmtAgo(m.created_at, now),
          ]}
          // 🔴 Mandant MUSS mit (gemeldet 25.08.2026): ohne ihn oeffnet sich die
          // Mail, waehrend die Liste auf dem zuletzt gewaehlten Betrieb stehen
          // bleibt — links der Reinigungsmandant, rechts eine Sanitec-Mail.
          onClick={() => {
            dismiss(`mail:${m.id}`)
            const q = new URLSearchParams({ open: String(m.id) })
            if (m.tenant_slug) q.set("tenant", m.tenant_slug)
            navigate(`/emails?${q.toString()}`)
          }}
          onDismiss={() => dismiss(`mail:${m.id}`)}
        />
      ))}

      {terminlinks.map((t) => (
        <BannerCard
          key={tlKey(t)}
          accent="#16a34a"
          icon={<CalendarCheck style={{ width: 18, height: 18 }} />}
          label={t.titel}
          tenant={tenantName(t.tenant_slug)}
          title={t.kunde}
          lines={[t.detail, t.vorgang, fmtAgo(t.zeit, now)].filter(Boolean) as string[]}
          onClick={() => {
            dismiss(tlKey(t))
            navigate(`/rueckmeldungen?tenant=${encodeURIComponent(t.tenant_slug)}`)
          }}
          onDismiss={() => dismiss(tlKey(t))}
        />
      ))}
    </div>
  )
}
